import { useEffect, useMemo, useState } from 'react'
import type { GameDefinition } from '../types'
import { useI18n } from '../i18n'
import { readNumber } from '../lib/storage'
import { buildChallengeUrl, isDateKey, utcDateKey } from '../lib/dailyChallenge'
import { trackGameEvent } from '../lib/analytics'

const bestScoreEvent = 'plays100:best-score'

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  const area = document.createElement('textarea')
  area.value = text
  area.style.position = 'fixed'
  area.style.opacity = '0'
  document.body.append(area)
  area.select()
  const copied = document.execCommand('copy')
  area.remove()
  if (!copied) throw new Error('copy failed')
}

export default function ChallengeShare({ game, challengeKey }: { game: GameDefinition; challengeKey?: string }) {
  const { lang } = useI18n()
  const dateKey = isDateKey(challengeKey) ? challengeKey : utcDateKey()
  const [best, setBest] = useState(() => readNumber(`best:${game.id}`))
  const [status, setStatus] = useState('')
  const url = useMemo(() => buildChallengeUrl(game.id, dateKey, lang), [dateKey, game.id, lang])

  useEffect(() => {
    setBest(readNumber(`best:${game.id}`))
    const update = (event: Event) => {
      const detail = (event as CustomEvent<{ key: string; score: number }>).detail
      if (detail?.key === `best:${game.id}`) setBest(detail.score)
    }
    window.addEventListener(bestScoreEvent, update)
    return () => window.removeEventListener(bestScoreEvent, update)
  }, [game.id])

  const share = async () => {
    const scoreLine = best > 0
      ? (lang === 'zh' ? `我的最高分：${best}` : `My best score: ${best}`)
      : (lang === 'zh' ? '来挑战我的成绩！' : 'Can you beat my run?')
    const text = lang === 'zh'
      ? `PlayFree 每日挑战 ${dateKey}｜${game.title.zh}\n${scoreLine}`
      : `PlayFree Daily Challenge ${dateKey} | ${game.title.en}\n${scoreLine}`
    try {
      if (navigator.share) {
        await navigator.share({ title: game.title[lang], text, url })
        trackGameEvent('share', game.id, { method: 'web_share', challenge_date: dateKey, score: best || undefined })
      } else {
        await copyText(`${text}\n${url}`)
        trackGameEvent('share', game.id, { method: 'clipboard', challenge_date: dateKey, score: best || undefined })
      }
      setStatus(lang === 'zh' ? '挑战链接已准备好' : 'Challenge link ready')
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      setStatus(lang === 'zh' ? '暂时无法分享，请稍后重试' : 'Sharing is unavailable right now')
    }
  }

  return <section className="challenge-share" aria-labelledby="challenge-share-title">
    <div className="challenge-score-card" style={{ background: `linear-gradient(145deg,${game.colors[0]},${game.colors[1]})` }}>
      <small>{lang === 'zh' ? '每日挑战' : 'DAILY CHALLENGE'} · {dateKey}</small>
      <strong id="challenge-share-title">{game.icon} {game.title[lang]}</strong>
      <span>{best > 0 ? `${lang === 'zh' ? '本机最高分' : 'Local best'} · ${best}` : (lang === 'zh' ? '完成一局后分享你的最高分' : 'Finish a run, then share your best')}</span>
    </div>
    <div><h2>{lang === 'zh' ? '挑战朋友' : 'Challenge a friend'}</h2><p>{lang === 'zh' ? '分享这张挑战卡和你的本机最高分，不会上传个人资料。' : 'Share this challenge and your local best. No personal profile is uploaded.'}</p><button className="btn btn-primary" type="button" onClick={share}>↗ {lang === 'zh' ? '分享成绩' : 'Share result'}</button><span className="share-status" role="status">{status}</span></div>
  </section>
}
